// The Today view (Blueprint §2 screen 1; parity F-TODAY-*, F-SCOPE-* read,
// F-ASGN pick-up/assign). Ports the SEMANTICS of legacy loadInbox/renderInbox/
// renderScopeMenu/renderUnassignedBanner/pickUpCase/openAssignModal (v20 lines
// ~1524-1653, 4520-4739) onto the 2.0 data layer (data/queue.js, data/scope.js,
// data/assign.js, data/users.js) + the store + the delegated data-action router.
//
// Structure on screen (top → bottom):
//   greeting (time-of-day + first name) · "N actions need you · worst first"
//   scope strip: scope pill ▾ (accessible menu) · amber legacy-pool banner ·
//                Patient board · Add patient
//   KPI tiles AS FILTER BUTTONS (Overdue/Due today/High risk/Unreachable/Waiting)
//   filter tabs (Overdue/Today/High risk/Unreachable/Waiting/All)
//   queue rows (avatar, programme dot, name, 🏥 facility · county · REF, action,
//               due pill, owner tag, type tag) — a serious AE row is visually
//               distinct, tagged "Safety · always shown", never hidden by scope
//   degradation notice when the owner-join failed (full queue still renders)
//
// SAFETY, inherited from the data layer and surfaced here honestly:
//  - a failed queue load → "Couldn't load your queue — tap to retry", never a
//    false all-clear;
//  - a failed owner-join → the FULL queue renders with a visible degradation
//    notice ("Owner details couldn't refresh … so nothing is hidden");
//  - a serious AE always renders regardless of scope.
//
// No inline handlers anywhere (strict CSP): every interactive element is a real
// control carrying data-action, dispatched by the one delegated router.

import { store, SLICES } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { register } from '../ui/actions.js';
import { openModal, closeModal, setBtnBusy } from '../ui/modal.js';
import { netErr, newRef } from '../errors.js';
import { canAssign } from '../auth.js';
import { registerView, navigate, countText } from './registry.js';
import { initials } from './shell.js';
import { progDotHTML } from '../programmes.js';
import { loadQueue, queueBucket, isSafetyQueueItem } from '../data/queue.js';
import {
  getScope,
  setScope,
  setScopeMode,
  scopeLabel,
  QS_MODES,
} from '../data/scope.js';
import { pickUpCase, reassignCase, quickAssignButtons } from '../data/assign.js';
import { loadUsers, userName, shortName, assignableUsers, usersMap } from '../data/users.js';
import { openWorkspace } from './workspace.js';
import { openReviewModal } from './consents.js';
import { showCoach, dismissCoach, COACH_SCOPE, COACH_SCOPE_TEXT } from '../ui/coachmark.js';

// ─── Pure view logic (unit-tested without a DOM) ───────────────────────────

/**
 * Time-of-day word for the greeting (legacy handleAccess greeting: <12 morning,
 * <17 afternoon, else evening).
 * @param {Date} d
 * @returns {'morning'|'afternoon'|'evening'}
 */
export function greetingWord(d) {
  const h = d.getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

/** First name from a full name (for the greeting). @param {string|null|undefined} full @returns {string} */
export function firstName(full) {
  const p = String(full || '').trim().split(/\s+/);
  return p[0] || 'there';
}

/**
 * "N actions need you" copy. The workload number is overdue + due-today (legacy
 * subtitle total = tod + ov) — the items that actually demand attention now.
 * @param {{ov?: number, tod?: number}} counts
 * @returns {string}
 */
export function actionsNeededText(counts) {
  const n = (counts && counts.ov ? counts.ov : 0) + (counts && counts.tod ? counts.tod : 0);
  if (n <= 0) return 'Your queue is clear';
  return `${n} action${n === 1 ? '' : 's'} need you`;
}

// The six filter tabs, in worst-first order, each mapped to the queueBucket key
// it lists. 'all' lists everything. Order + rules are the legacy classification,
// kept verbatim (F-TODAY-04).
export const TABS = Object.freeze([
  Object.freeze({ id: 'overdue', label: 'Overdue', bucket: 'overdue', countKey: 'ov' }),
  Object.freeze({ id: 'today', label: 'Today', bucket: 'today', countKey: 'tod' }),
  Object.freeze({ id: 'risk', label: 'High risk', bucket: 'risk', countKey: 'risk' }),
  Object.freeze({ id: 'unreachable', label: 'Unreachable', bucket: 'unreachable', countKey: 'unr' }),
  Object.freeze({ id: 'waiting', label: 'Waiting', bucket: 'waiting', countKey: 'wait' }),
  Object.freeze({ id: 'all', label: 'All', bucket: null, countKey: null }),
]);

// The five KPI tiles (filter buttons). Same order as legacy k-tod…: but shown
// worst-first (Overdue leads). danger=true styles the Overdue tile red.
export const TILES = Object.freeze([
  Object.freeze({ tab: 'overdue', label: 'Overdue', countKey: 'ov', hint: 'act first', dot: 'danger', danger: true }),
  Object.freeze({ tab: 'today', label: 'Due today', countKey: 'tod', hint: 'clear by 5 pm', dot: 'warn', danger: false }),
  Object.freeze({ tab: 'risk', label: 'High risk', countKey: 'risk', hint: 'review', dot: 'info', danger: false }),
  Object.freeze({ tab: 'unreachable', label: 'Unreachable', countKey: 'unr', hint: '3+ failed', dot: 'muted', danger: false }),
  Object.freeze({ tab: 'waiting', label: 'Waiting', countKey: 'wait', hint: 'in SLA', dot: 'ok', danger: false }),
]);

/**
 * Worst-first rank for a row's bucket (lower = more urgent). A serious AE always
 * sorts to the very top regardless of bucket.
 * @param {string} bucket queueBucket() result
 * @returns {number}
 */
export function bucketRank(bucket) {
  const order = { overdue: 0, risk: 1, today: 2, unreachable: 3, waiting: 4 };
  return bucket in order ? order[bucket] : 5;
}

/**
 * Sort a row set worst-first: serious AE first, then bucket severity, then the
 * soonest due_at (the data layer already ordered by due; this makes the visible
 * "worst first" ordering explicit and stable). Pure — returns a new array.
 * @param {any[]} rows enriched queue rows
 * @param {Date} now
 * @returns {any[]}
 */
export function sortWorstFirst(rows, now) {
  return (rows || [])
    .map((it, i) => ({ it, i }))
    .sort((a, b) => {
      const sa = isSafetyQueueItem(a.it) ? 0 : 1;
      const sb = isSafetyQueueItem(b.it) ? 0 : 1;
      if (sa !== sb) return sa - sb;
      const ra = bucketRank(queueBucket(a.it, now));
      const rb = bucketRank(queueBucket(b.it, now));
      if (ra !== rb) return ra - rb;
      const da = new Date(a.it.due_at).getTime() || 0;
      const db = new Date(b.it.due_at).getTime() || 0;
      if (da !== db) return da - db;
      return a.i - b.i; // stable
    })
    .map((x) => x.it);
}

/**
 * Rows visible under the active tab (F-TODAY-04 classification via queueBucket).
 * 'all' returns everything. Pure.
 * @param {any[]} rows
 * @param {string} tab active tab id
 * @param {Date} now
 * @returns {any[]}
 */
export function rowsForTab(rows, tab, now) {
  if (tab === 'all') return rows || [];
  return (rows || []).filter((it) => queueBucket(it, now) === tab);
}

/**
 * Is this queue row a consent-review task (no case yet)? Exact legacy predicate
 * (renderInbox): a consent/intake row, or a review task with no reference.
 * @param {any} it
 * @returns {boolean}
 */
export function isConsentItem(it) {
  const qt = (it && it.queue_type ? String(it.queue_type) : '').toLowerCase();
  const al = (it && it.action_label ? String(it.action_label) : '').toLowerCase();
  return qt.includes('consent') || al.includes('consent intake') || (!it.reference_number && al.includes('review'));
}

/**
 * Due pill class + label (legacy dueL + the tag colouring). Overdue/high-risk
 * queue types and any past-due row are danger; a same-day row is warn ("Today").
 * @param {any} it
 * @param {Date} now
 * @returns {{cls: 'over'|'today'|'ok', label: string}}
 */
export function dueTag(it, now) {
  const qt = (it.queue_type || '').toLowerCase();
  const due = new Date(it.due_at);
  const forcedOverdue = qt === 'sha_overdue' || qt === 'overdue_consent' || qt === 'missed_followup';
  if (forcedOverdue || due < now) return { cls: 'over', label: dueLabel(it.due_at) };
  if (due.toDateString() === now.toDateString()) return { cls: 'today', label: 'Today' };
  return { cls: 'ok', label: dueLabel(it.due_at) };
}

/**
 * Human due label (exact legacy dueL). "6d overdue", "in 3h", "12 Jul", …
 * @param {string|null|undefined} d ISO timestamp
 * @param {Date} [now] injectable for tests
 * @returns {string}
 */
export function dueLabel(d, now) {
  if (!d) return '—';
  const base = now ? now.getTime() : Date.now();
  const diff = (new Date(d).getTime() - base) / 1000;
  if (diff < -86400) return Math.floor(-diff / 86400) + 'd overdue';
  if (diff < 0) return Math.floor(-diff / 3600) + 'h overdue';
  if (diff < 3600) return 'in ' + Math.floor(diff / 60) + 'm';
  if (diff < 86400) return 'in ' + Math.floor(diff / 3600) + 'h';
  if (diff < 86400 * 7) return 'in ' + Math.floor(diff / 86400) + 'd';
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * Type tag (legacy renderInbox): serious AE → Safety; consent → Consent;
 * sha_overdue → High risk; else Case.
 * @param {any} it
 * @returns {{cls: string, label: string}}
 */
export function typeTag(it) {
  if (isSafetyQueueItem(it)) return { cls: 'serious', label: 'Safety · always shown' };
  if (isConsentItem(it)) return { cls: 'accent', label: 'Consent' };
  if ((it.queue_type || '').toLowerCase() === 'sha_overdue') return { cls: 'danger', label: 'High risk' };
  return { cls: 'case', label: 'Case' };
}

/**
 * The "🏥 facility · county · REF" subline facts (legacy renderInbox loc/subline).
 * A consent row shows "Pending consent" instead of a reference.
 * @param {any} it enriched row (facility/county folded in by the loader)
 * @returns {{loc: string, ref: string}}
 */
export function rowFacts(it) {
  const loc = [it.facility || 'No facility', it.county].filter(Boolean).join(' · ');
  const ref = isConsentItem(it) ? 'Pending consent' : it.reference_number || '—';
  return { loc, ref };
}

/**
 * What the queue card may show for a QUEUE slice. PURE — this is the gate that
 * stops a false all-clear: an ABSENT or not-yet-resolved slice is 'loading',
 * never 'ready'. Before this existed the card rendered from `q.rows || []`, so
 * every sign-in, route change and Sync painted "All clear — you're on track"
 * and "Your queue is clear" until the first chunk of v_ct_queue_all landed —
 * seconds on a clinic connection, indefinitely if the request hung (a hang is
 * neither ok nor loadError). Board and Patients already guard this with
 * casesLoaded(); Today now does too.
 * @param {any} slice SLICES.QUEUE value (may be undefined)
 * @returns {'loading'|'error'|'ready'}
 */
export function queueCardState(slice) {
  const q = slice || {};
  if (q.loadError) return 'error';
  if (!q.loaded) return 'loading';
  return 'ready';
}

/**
 * Decide the empty-state species (F-PAT-07-style distinction applied to Today):
 *  - 'load-error' — the queue query failed (handled before this is reached);
 *  - 'tab-empty'  — this tab is empty but other tabs have work;
 *  - 'scoped'     — the WHOLE scoped queue is empty but items exist outside the
 *                   current scope (a way out is offered);
 *  - 'clear'      — genuinely nothing anywhere: "All clear — you're on track."
 * Pure.
 * @param {{scopedCount: number, allCount: number, visibleCount: number, scopeMode: string}} ctx
 * @returns {'tab-empty'|'scoped'|'clear'}
 */
export function emptyStateKind(ctx) {
  if (ctx.visibleCount > 0) return 'tab-empty'; // caller shouldn't ask, but safe
  if (ctx.scopedCount > 0) return 'tab-empty'; // other tabs have work
  // Nothing in the scoped queue at all.
  if (ctx.allCount > ctx.scopedCount && ctx.scopeMode !== 'all') return 'scoped';
  return 'clear';
}

// ─── SVG icons (no functional emoji per §3; inline, CSP-safe) ───────────────

/** @param {string} paths @param {number} [size] @returns {string} */
function ic(paths, size = 16) {
  return (
    `<svg class="ic" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" ` +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    paths +
    '</svg>'
  );
}
const IC_HOSPITAL = '<path d="M3 21V8l7-4 7 4v13M7 21v-6h6v6M21 21V11l-4-2.3"/>';
const IC_USER = '<circle cx="12" cy="8" r="3.5"/><path d="M5 19c.8-3.4 3.3-5.2 7-5.2s6.2 1.8 7 5.2"/>';
const IC_WARN = '<path d="M12 4L2.5 20h19z"/><path d="M12 10v4m0 3v.5"/>';
const IC_INFO = '<circle cx="12" cy="12" r="9"/><path d="M12 8v5m0 3v.5"/>';
const IC_CHEVRON = '<polyline points="6 9 12 15 18 9"/>';
const IC_BOARD = '<rect x="3" y="4" width="5" height="16" rx="1"/><rect x="10" y="4" width="5" height="10" rx="1"/><rect x="17" y="4" width="4" height="13" rx="1"/>';
const IC_PLUS = '<path d="M12 5v14M5 12h14"/>';

/** Deterministic avatar colour from a name (self-contained port of legacy ac()). */
function avatarColor(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h} 42% 42%)`;
}

// ─── Module state ──────────────────────────────────────────────────────────

let _activeTab = 'all'; // worst-first, everything visible by default
let _menuOpen = false;
let _degradeRef = /** @type {string|null} */ (null); // stable TU-ref for the current fail-open render
let _wired = false;
let _pickingUp = new Set(); // case ids with an in-flight pick-up (button lock)

/** Test-only: reset module state. @returns {void} */
export function _resetToday() {
  _activeTab = 'all';
  _menuOpen = false;
  _degradeRef = null;
  _wired = false;
  _pickingUp = new Set();
}

function doc() {
  return /** @type {any} */ (globalThis).document;
}

/** Is the Today view currently the mounted view? (guards store-driven re-renders). */
function todayMounted() {
  const d = doc();
  const ui = store.get(SLICES.UI) || {};
  return !!d && ui.view === 'today' && !!d.getElementById('today-view');
}

function session() {
  return store.get(SLICES.SESSION) || {};
}
function selfId() {
  const s = session();
  return s.user && s.user.id;
}
function role() {
  return session().role;
}
function scope() {
  return getScope(selfId(), role());
}

// ─── Renderers (store → DOM) ───────────────────────────────────────────────

function renderGreeting() {
  const d = doc();
  const el = d && d.getElementById('today-greet');
  if (!el) return;
  const now = new Date();
  const sess = session();
  const q = store.get(SLICES.QUEUE) || {};
  const counts = q.counts || {};
  const dateStr = now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  // The workload sentence only once the queue has actually resolved: "Your
  // queue is clear" over an in-flight load is the same false all-clear as the
  // empty card (see queueCardState).
  const need = queueCardState(q) === 'ready' ? ` · <b>${esc(actionsNeededText(counts))}</b> · worst first` : '';
  el.innerHTML =
    `<h1 class="today-h">Good <em>${esc(greetingWord(now))}</em>, ${esc(firstName(sess.fullName))}</h1>` +
    `<p class="today-sub">${esc(dateStr)}${need}</p>`;
}

/** Scope pill label + the legacy-pool banner state. */
function renderScopeStrip() {
  const d = doc();
  const pill = d && d.getElementById('today-scope-pill');
  if (pill) {
    const lbl = d.getElementById('today-scope-lbl');
    if (lbl) lbl.textContent = 'Queue: ' + scopeLabel(scope());
  }
  renderBanner();
}

function renderBanner() {
  const d = doc();
  const el = d && d.getElementById('today-banner');
  if (!el) return;
  const q = store.get(SLICES.QUEUE) || {};
  const n = (q.counts && q.counts.legacyPool) || 0;
  const mode = scope().mode;
  // Shown whenever there ARE ownerless legacy patients and the nurse is looking
  // at a personal scope (mine / mine + colleagues) where those patients are
  // hidden — the way to find and pick them up. (Task §2.)
  const show = n > 0 && (mode === 'mine' || mode === 'mine_plus');
  if (!show) {
    el.hidden = true;
    el.innerHTML = '';
    return;
  }
  el.hidden = false;
  el.innerHTML =
    `<button type="button" class="today-banner-btn" data-action="today-drain" ` +
    `aria-label="${esc(n + ' legacy patient' + (n === 1 ? '' : 's') + ' have no owner — view and pick up')}">` +
    ic(IC_WARN, 16) +
    `<span><b>${esc(String(n))} legacy patient${n === 1 ? '' : 's'}</b> have no owner</span>` +
    '<span class="today-banner-go">View &amp; pick up ›</span>' +
    '</button>';
}

function renderTiles() {
  const d = doc();
  const host = d && d.getElementById('today-tiles');
  if (!host) return;
  const q = store.get(SLICES.QUEUE) || {};
  const counts = q.counts || {};
  const state = queueCardState(q);
  host.innerHTML = TILES.map((t) => {
    const active = _activeTab === t.tab;
    // A tile reading "0" is a false all-clear in miniature — whether the queue
    // is still loading or the load FAILED. Only 'ready' prints a number.
    const val = countText(state, counts[t.countKey] || 0);
    return (
      `<button type="button" class="today-tile${t.danger ? ' danger' : ''}${active ? ' active' : ''}" ` +
      `data-action="today-tab" data-tab="${esc(t.tab)}" aria-pressed="${active}">` +
      `<span class="tt-label"><span class="tt-dot ${t.dot}"></span>${esc(t.label)}</span>` +
      `<span class="tt-val">${esc(String(val))}</span>` +
      `<span class="tt-hint">${esc(t.hint)}</span>` +
      '</button>'
    );
  }).join('');
}

function renderCard() {
  const d = doc();
  const host = d && d.getElementById('today-card');
  if (!host) return;
  const q = store.get(SLICES.QUEUE) || {};
  const state = queueCardState(q);

  // FIRST LOAD STILL IN FLIGHT — a skeleton, never the empty state. (Also the
  // state on every fresh boot: the slice does not exist until loadQueue lands.)
  if (state === 'loading') {
    host.innerHTML =
      '<div class="today-rows" aria-busy="true" aria-label="Loading your queue">' +
      '<div class="skel"></div><div class="skel"></div><div class="skel"></div></div>';
    return;
  }

  if (state === 'error') {
    host.innerHTML =
      '<button type="button" class="today-error" data-action="today-retry">' +
      ic(IC_WARN, 22) +
      '<div><h2>Couldn’t load your queue — tap to retry</h2>' +
      '<p>The query failed or was denied. Nothing is hidden — tap to try again.</p></div>' +
      '</button>';
    return;
  }

  const now = new Date();
  const counts = q.counts || {};
  const allScoped = q.rows || [];
  const tabs = TABS.map((t) => {
    const n = t.countKey ? counts[t.countKey] || 0 : allScoped.length;
    const active = _activeTab === t.id;
    return (
      `<button type="button" class="today-tab${active ? ' active' : ''}" data-action="today-tab" ` +
      `data-tab="${esc(t.id)}" aria-pressed="${active}">${esc(t.label)}` +
      `<span class="tb-n">${esc(String(n))}</span></button>`
    );
  }).join('');

  const visible = sortWorstFirst(rowsForTab(allScoped, _activeTab, now), now);
  const body = visible.length
    ? '<div class="today-rhdr"><span>Patient</span><span>Action</span><span>Due</span><span>Owner</span><span></span></div>' +
      visible.map((it) => rowHTML(it, now)).join('')
    : emptyHTML({
        scopedCount: allScoped.length,
        allCount: (q.all || []).length,
        visibleCount: 0,
        scopeMode: scope().mode,
      });

  const degrade = q.ownerJoinFailed ? degradeHTML() : '';

  host.innerHTML =
    `<div class="today-tabs" role="group" aria-label="Filter the queue">${tabs}</div>` +
    `<div class="today-rows">${body}</div>` +
    degrade;
  // Apply each avatar's colour via the CSSOM. An inline style= attribute in the
  // row HTML would be blocked by CSP style-src 'self'; a script-driven .style
  // mutation is not governed by that policy.
  host.querySelectorAll('.tr-av[data-avatar]').forEach((el) => {
    /** @type {any} */ (el).style.background = el.getAttribute('data-avatar');
  });
}

/** One queue row. @param {any} it enriched row @param {Date} now @returns {string} */
function rowHTML(it, now) {
  const consent = isConsentItem(it);
  const safety = isSafetyQueueItem(it);
  const facts = rowFacts(it);
  const due = dueTag(it, now);
  const tt = typeTag(it);
  const caseId = it.record_id;
  const name = it.patient_name || '—';
  const action = String(it.action_label || '').replace(/_/g, ' ');

  // Owner tag — hidden in 'mine' scope (every row is yours); an owned row shows
  // "First L.", an unowned one shows an amber "Unassigned". Safety rows keep the
  // owner tag too but their Safety type tag is what matters.
  let ownerTag = '';
  if (!consent && scope().mode !== 'mine') {
    ownerTag = it.owner
      ? `<span class="today-tag owner">${ic(IC_USER, 12)}${esc(shortName(userName(it.owner)))}</span>`
      : '<span class="today-tag unassigned">Unassigned</span>';
  }

  // Per-row quick actions (legacy quickAssignButtons): only in the Legacy-pool
  // scope, only on real unowned cases (consent items have no case; safety items
  // bypass scope). All staff get Pick up + Assign ▾; coordinators/admins get
  // Assign ▾ only (they route rather than self-hold).
  const quick = quickAssignButtons({
    scopeMode: scope().mode,
    isConsent: consent,
    isSafety: safety,
    canAssign: canAssign(role()),
  });
  const picking = _pickingUp.has(caseId);
  const quickBtns = quick
    .map((k) =>
      k === 'pickup'
        ? `<button type="button" class="btn sm" data-action="today-pickup" data-case="${esc(caseId)}"${picking ? ' disabled' : ''}>${picking ? 'Picking up…' : 'Pick up'}</button>`
        : `<button type="button" class="btn sm" data-action="today-assign" data-case="${esc(caseId)}" data-name="${esc(name)}" data-unassigned="1">Assign ▾</button>`
    )
    .join('');

  const primaryLabel = consent ? 'Review' : 'Open';
  const primary = `<button type="button" class="btn sm pri" data-action="today-open" data-case="${esc(caseId)}" data-consent="${consent ? '1' : '0'}" data-name="${esc(name)}">${primaryLabel}</button>`;

  const dot = it.programme_code ? progDotHTML(it.programme_code, programmeHex(it.programme_code), 8) : '';

  return (
    `<div class="today-row${safety ? ' serious' : ''}" role="button" tabindex="0" data-kbd-click ` +
    `data-action="today-open" data-case="${esc(caseId)}" data-consent="${consent ? '1' : '0'}" data-name="${esc(name)}" ` +
    `aria-label="${esc(name + ' — open workspace')}">` +
    '<div class="tr-pc">' +
    `<div class="tr-av" aria-hidden="true" data-avatar="${esc(avatarColor(name))}">${esc(initials(name))}</div>` +
    '<div class="tr-pi">' +
    `<div class="tr-name">${dot}<span>${esc(name)}</span></div>` +
    `<div class="tr-sub">${ic(IC_HOSPITAL, 13)}<span>${esc(facts.loc)} · ${esc(facts.ref)}</span>` +
    `<span class="m-only"> · ${esc(action)} · ${esc(due.label)}</span></div>` +
    '</div></div>' +
    `<div class="tr-act">${esc(action)}</div>` +
    `<div class="tr-due"><span class="today-due ${due.cls}">${esc(due.label)}</span></div>` +
    `<div class="tr-owner">${ownerTag}<span class="today-tag ${tt.cls}">${esc(tt.label)}</span></div>` +
    `<div class="tr-cta">${quickBtns}${primary}</div>` +
    '</div>'
  );
}

/** Look up a programme's validated accent hex from the loaded catalogue. */
function programmeHex(code) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const p = prog.byCode && prog.byCode[code];
  return p && p.brand_accent_hex;
}

function degradeHTML() {
  if (!_degradeRef) _degradeRef = newRef();
  return (
    '<div class="today-degrade">' +
    ic(IC_INFO, 16) +
    '<span>Owner details couldn’t refresh — showing the full queue so nothing is hidden.</span>' +
    '<button type="button" class="today-degrade-retry" data-action="today-retry">Retry</button>' +
    `<span class="today-degrade-ref">ref ${esc(_degradeRef)}</span>` +
    '</div>'
  );
}

/** @param {{scopedCount: number, allCount: number, visibleCount: number, scopeMode: string}} ctx */
function emptyHTML(ctx) {
  const kind = emptyStateKind(ctx);
  if (kind === 'scoped') {
    const hidden = ctx.allCount - ctx.scopedCount;
    return (
      '<div class="today-empty">' +
      ic(IC_USER, 22) +
      '<h2>Nothing in your queue right now</h2>' +
      `<p>${esc(String(hidden))} patient${hidden === 1 ? ' is' : 's are'} outside your current scope. ` +
      'Widen the view to see the whole team’s work.</p>' +
      '<button type="button" class="btn sm" data-action="today-scope-set" data-mode="all">Show everyone</button>' +
      '</div>'
    );
  }
  if (kind === 'tab-empty') {
    const label = (TABS.find((t) => t.id === _activeTab) || { label: 'this filter' }).label;
    return (
      '<div class="today-empty">' +
      ic(IC_INFO, 22) +
      `<h2>No ${esc(label.toLowerCase())} items</h2>` +
      '<p>Nothing in this filter — nice work. Other tabs may still have items.</p>' +
      '</div>'
    );
  }
  return (
    '<div class="today-empty">' +
    ic(IC_INFO, 22) +
    '<h2>All clear — you’re on track</h2>' +
    '<p>No items in your queue. New work appears here as it arrives.</p>' +
    '</div>'
  );
}

/** Re-render every store-driven region (called on QUEUE changes). */
function renderBody() {
  if (!todayMounted()) return;
  const q = store.get(SLICES.QUEUE) || {};
  // Keep the fail-open ref stable across tab clicks; drop it once healthy.
  if (!q.ownerJoinFailed) _degradeRef = null;
  renderGreeting();
  renderScopeStrip();
  renderTiles();
  renderCard();
}

// ─── Scope menu (accessible dropdown, ported from programmes.js pattern) ────

function menuEl() {
  const d = doc();
  return d && d.getElementById('today-scope-menu');
}
function pillEl() {
  const d = doc();
  return d && d.getElementById('today-scope-pill');
}

function renderScopeMenu() {
  const m = menuEl();
  if (!m) return;
  const s = scope();
  const opt = (mode, label, desc) => {
    const on = s.mode === mode;
    return (
      `<button type="button" role="menuitemradio" aria-checked="${on}" class="today-scope-opt${on ? ' on' : ''}" ` +
      `data-action="today-scope-set" data-mode="${esc(mode)}">` +
      `<span class="tso-radio" aria-hidden="true">${on ? '●' : '○'}</span>` +
      `<span class="tso-txt"><span class="tso-name">${esc(label)}</span>` +
      (desc ? `<span class="tso-desc">${esc(desc)}</span>` : '') +
      '</span></button>'
    );
  };
  const n = (store.get(SLICES.QUEUE) || {}).counts;
  const poolN = (n && n.legacyPool) || 0;
  const me = selfId();
  const colleagues = assignableUsers(usersMap())
    .filter((u) => u.user_id !== me)
    .map((u) => {
      const on = s.extra.includes(u.user_id);
      return (
        `<label class="today-scope-chk"><input type="checkbox" ` +
        `data-action="today-scope-colleague" data-uid="${esc(u.user_id)}" value="${esc(u.user_id)}"${on ? ' checked' : ''}/> ` +
        `<span>${esc(u.full_name || '—')}</span></label>`
      );
    })
    .join('') || '<div class="tso-desc tso-empty">No colleagues found</div>';

  m.innerHTML =
    opt('mine', QS_MODES.mine, 'Cases assigned to you') +
    opt('mine_plus', 'Mine + colleagues…', 'Yours plus selected teammates') +
    (s.mode === 'mine_plus' ? `<div class="today-scope-chks" role="group" aria-label="Include colleagues">${colleagues}</div>` : '') +
    opt('all', QS_MODES.all, 'All cases, all nurses') +
    opt('unassigned', QS_MODES.unassigned + (poolN > 0 ? ` (${poolN})` : ''), 'Legacy & exceptions — normally empty');
}

function openMenu() {
  const m = menuEl();
  const p = pillEl();
  if (!m || !p) return;
  renderScopeMenu();
  m.hidden = false;
  _menuOpen = true;
  p.setAttribute('aria-expanded', 'true');
  const checked = m.querySelector('[aria-checked="true"]') || m.querySelector('[role="menuitemradio"]');
  if (checked) /** @type {any} */ (checked).focus();
}

/** @param {boolean} [refocus] */
function closeMenu(refocus) {
  const m = menuEl();
  const p = pillEl();
  if (m) m.hidden = true;
  _menuOpen = false;
  if (p) {
    p.setAttribute('aria-expanded', 'false');
    if (refocus) p.focus();
  }
}

// ─── Actions (data-action handlers) ─────────────────────────────────────────

/** Reload the queue and re-render (never throws; the loader owns error state). */
async function refresh() {
  // The envelope is RETURNED, not dropped: loadQueue never throws (a failure
  // lands as loadError on the slice), so Sync can only tell a failed refresh
  // from a good one by reading this. See registry.firstFailure().
  const res = await loadQueue();
  renderBody();
  return res;
}

function onTab(el) {
  const tab = el.getAttribute('data-tab');
  if (!tab || !TABS.some((t) => t.id === tab)) return;
  _activeTab = tab;
  renderTiles();
  renderCard();
}

async function onScopeSet(el) {
  const mode = el.getAttribute('data-mode') || 'mine';
  closeMenu(true);
  setScopeMode(selfId(), mode, role());
  renderScopeStrip();
  renderTiles();
  await refresh();
}

async function onColleague(el) {
  const uid = el.getAttribute('data-uid');
  if (!uid) return;
  const s = scope();
  const on = /** @type {any} */ (el).checked;
  // No aria-checked patch here: this is a NATIVE checkbox, so the browser maps
  // its checked PROPERTY — already flipped by the click — for assistive tech. The
  // attribute this line used to write was a second source of truth that drifted:
  // the property changed on every click, the attribute only when this ran.
  const extra = on ? [...new Set([...s.extra, uid])] : s.extra.filter((x) => x !== uid);
  setScope(selfId(), { mode: 'mine_plus', extra }, undefined);
  // Keep the menu open (only its checkbox state changed) and refresh the queue.
  renderScopeStrip();
  renderTiles();
  await refresh();
}

async function onPickup(el) {
  const caseId = el.getAttribute('data-case');
  if (!caseId || _pickingUp.has(caseId)) return;
  _pickingUp.add(caseId);
  renderCard(); // reflect the "Picking up…" lock
  const res = await pickUpCase(caseId);
  _pickingUp.delete(caseId);
  if (res.ok) {
    toast('Patient assigned to you', 'ok');
  } else if (res.code === 'TAKEN') {
    toast('A colleague already picked this patient up — refreshing', 'err');
  } else {
    toast(netErr(res.error, 'Could not pick up this patient'), 'err');
  }
  await refresh();
}

function onOpen(el) {
  const name = el.getAttribute('data-name') || 'this patient';
  const consent = el.getAttribute('data-consent') === '1';
  const caseId = el.getAttribute('data-case');
  // A real case opens the Workspace overlay (Blueprint screen 5, Task C). A
  // consent-review item has no case yet — it opens the intake review modal
  // (Phase 3 Task A), whose record_id IS the submission id (legacy openIntakeModal).
  if (!consent && caseId) {
    void openWorkspace(caseId);
    return;
  }
  if (consent && caseId) {
    void openReviewModal(caseId);
    return;
  }
  toast(`Couldn’t open the consent review for ${name}`, 'err');
}

/** Open the assign / reassign modal (ported from legacy openAssignModal). */
function onAssign(el) {
  const caseId = el.getAttribute('data-case');
  const name = el.getAttribute('data-name') || '';
  const unassigned = el.getAttribute('data-unassigned') === '1';
  const curOwner = unassigned ? null : el.getAttribute('data-owner') || null;
  openAssignModal(caseId, name, curOwner);
}

/**
 * Assign / reassign modal. Ported semantics: one verb open to all staff; a note
 * is REQUIRED (and the modal stays open) when moving a colleague's patient; the
 * write is claim-safe (unowned → TAKEN) or expected-owner guarded (owned →
 * STALE); a failed save keeps the modal + typed input (setBtnBusy pattern).
 * @param {string|null} caseId
 * @param {string} name
 * @param {string|null} curOwner
 */
function openAssignModal(caseId, name, curOwner) {
  if (!caseId) return;
  const me = selfId();
  const movingOthers = !!curOwner && curOwner !== me;
  const options = assignableUsers(usersMap())
    .map((u) => `<option value="${esc(u.user_id)}">${esc(u.full_name || '—')} (${esc(u.role || 'nurse')})</option>`)
    .join('');
  const noteHint = movingOthers
    ? `<p class="today-assign-hint">You’re moving ${esc(shortName(userName(curOwner)))}’s patient — say why.</p>`
    : '';
  const body =
    `<p class="today-assign-lead">Choose the nurse who will own this case.</p>` +
    '<div class="field"><label>Assign to</label>' +
    `<select id="today-assign-nurse"><option value="">Select nurse…</option>${options}</select></div>` +
    `<div class="field"><label>Note for the timeline${movingOthers ? ' *' : ''}</label>` +
    '<textarea id="today-assign-note" rows="3" placeholder="Add a note for the timeline (optional)"></textarea>' +
    noteHint +
    '</div>';

  openModal(
    curOwner ? 'Reassign patient' : 'Assign patient',
    name || '',
    body,
    [
      { l: 'Cancel', k: 'cancel', fn: () => closeModal() },
      {
        l: curOwner ? 'Move…' : 'Assign',
        v: 'pri',
        k: 'assign',
        fn: () => submitAssign(caseId, curOwner),
      },
    ]
  );
}

async function submitAssign(caseId, curOwner) {
  const d = doc();
  const sel = d && d.getElementById('today-assign-nurse');
  const uid = sel ? sel.value : '';
  if (!uid) {
    toast('Select a nurse', 'err');
    return;
  }
  const note = (d && d.getElementById('today-assign-note') && d.getElementById('today-assign-note').value.trim()) || '';
  setBtnBusy(true, 'Assigning…', 'assign');
  const res = await reassignCase(caseId, uid, { curOwner, note }, undefined);
  if (res.ok) {
    closeModal();
    const moved = !!curOwner;
    const okMsg = moved
      ? uid === selfId()
        ? 'Added to your queue'
        : 'Moved to ' + userName(uid)
      : 'Assigned to ' + userName(uid);
    if (res.data && res.data.noteFailed) {
      toast(okMsg + ' — but the note did NOT save. Add it via Patient notes.', 'err', 12000);
    } else {
      toast(okMsg, 'ok');
    }
    await refresh();
    return;
  }
  // Non-error guard misses: close + inform (a refresh follows). Real errors keep
  // the modal open with the typed note intact (setBtnBusy pattern).
  if (res.code === 'TAKEN') {
    setBtnBusy(false);
    closeModal();
    toast('A colleague already picked this patient up — refreshing', 'err');
    await refresh();
  } else if (res.code === 'STALE') {
    setBtnBusy(false);
    closeModal();
    toast('Ownership just changed — refreshing', 'err');
    await refresh();
  } else if (res.code === 'NOTE_REQUIRED') {
    setBtnBusy(false);
    toast('You’re moving another nurse’s patient — say why', 'err');
  } else {
    setBtnBusy(false);
    toast(netErr(res.error, 'Could not assign patient'), 'err');
  }
}

// ─── Wiring (once) ──────────────────────────────────────────────────────────

function wireOnce() {
  if (_wired) return;
  _wired = true;
  const d = doc();

  register('today-tab', onTab);
  register('today-retry', () => void refresh());
  register('today-scope-toggle', () => {
    // Opening the scope menu IS the thing the coachmark was pointing at — the
    // hint has done its job, so it gets out of the way immediately rather than
    // sitting under the menu it just explained.
    dismissCoach();
    return _menuOpen ? closeMenu() : openMenu();
  });
  register('coach-dismiss', () => dismissCoach());
  register('today-scope-set', (el) => void onScopeSet(el));
  // Legacy-pool DRAIN: set the shared scope to the pool and deep-link into the
  // Patients registry — the surface built to work ownerless cases down (Task B §2).
  register('today-drain', () => {
    setScopeMode(selfId(), 'legacy', role());
    navigate('patients');
  });
  register('today-scope-colleague', (el) => void onColleague(el));
  register('today-pickup', (el) => void onPickup(el));
  register('today-assign', onAssign);
  register('today-open', onOpen);

  // Re-render on queue changes (the loader publishes SLICES.QUEUE).
  store.subscribe(SLICES.QUEUE, () => renderBody());

  // Realtime: reload when the queue's owned keys change (legacy 'inbox' key).
  store.subscribe(SLICES.RT_CHANGES, (v) => {
    if (todayMounted() && v && Array.isArray(v.changed) && v.changed.includes('inbox')) {
      void refresh();
    }
  });

  // Scope-menu keyboard model (mirrors programmes.js): capture-phase Escape so
  // it closes the MENU only (never also a modal underneath); Tab closes;
  // arrows/Home/End move between the radio options.
  if (d) {
    d.addEventListener(
      'keydown',
      (/** @type {any} */ e) => {
        if (!_menuOpen) return;
        if (e.key === 'Escape') {
          e.stopPropagation();
          closeMenu(true);
        } else if (e.key === 'Tab') {
          closeMenu(true);
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
          const m = menuEl();
          // Roving set includes the colleague checkboxes so a keyboard/SR user can
          // reach and toggle them — not just the radios. They are matched by
          // SELECTOR, not by a role: they are native checkboxes (A-18), and a role
          // override is exactly what stopped the browser describing their own state.
          const items = m
            ? [...m.querySelectorAll('[role="menuitemradio"],.today-scope-chk input[type="checkbox"]')]
            : [];
          if (!items.length) return;
          e.preventDefault();
          let next;
          if (e.key === 'Home') next = items[0];
          else if (e.key === 'End') next = items[items.length - 1];
          else {
            const i = items.indexOf(d.activeElement);
            next = e.key === 'ArrowDown' ? items[(i + 1) % items.length] : items[(i - 1 + items.length) % items.length];
          }
          /** @type {any} */ (next).focus();
        }
      },
      true
    );
    // Outside click closes the menu.
    d.addEventListener('click', (/** @type {any} */ e) => {
      if (!_menuOpen) return;
      const t = e.target;
      if (t && typeof t.closest === 'function' && t.closest('.today-scope-wrap')) return;
      closeMenu();
    });
  }
}

// ─── View registration ──────────────────────────────────────────────────────

/**
 * The Today view's static skeleton. The store-driven regions (greeting, scope
 * label, banner, tiles, card) are filled by the renderers; the scope menu is
 * managed independently so an open menu survives a queue re-render.
 * @returns {string}
 */
function skeleton() {
  return (
    '<div id="today-view" class="today">' +
    '<div id="today-greet" class="today-greet"></div>' +
    '<div class="today-scope-strip">' +
    '<span class="today-scope-wrap">' +
    '<button type="button" class="today-scope-pill" id="today-scope-pill" data-action="today-scope-toggle" ' +
    'aria-haspopup="menu" aria-expanded="false">' +
    ic(IC_USER, 14) +
    '<span id="today-scope-lbl">Queue</span>' +
    ic(IC_CHEVRON, 14) +
    '</button>' +
    '<div class="today-scope-menu" id="today-scope-menu" role="menu" aria-label="Queue scope" hidden></div>' +
    '</span>' +
    '<span id="today-banner" class="today-banner" hidden></span>' +
    `<button type="button" class="btn" data-action="today-board">${ic(IC_BOARD, 15)}Patient board</button>` +
    `<button type="button" class="btn pri" data-action="today-add">${ic(IC_PLUS, 15)}Add patient</button>` +
    '</div>' +
    '<div id="today-tiles" class="today-tiles"></div>' +
    '<div id="today-card" class="today-card"></div>' +
    '</div>'
  );
}

/**
 * Register the Today view, replacing the Phase-1 placeholder. Call once at boot
 * (after the placeholder loop) from main.js.
 * @returns {void}
 */
export function registerToday() {
  // Board + Add-patient live outside the queue lifecycle — wire them here so
  // they exist even before the first load. (Add patient routes to the consent
  // intake queue for now — the dedicated "new patient" flow lands in Phase 3.)
  register('today-board', () => navigate('board'));
  register('today-add', () => {
    toast('Add patient — starting from New consents', '');
    navigate('consents');
  });

  registerView('today', {
    render(host) {
      host.innerHTML = skeleton();
      wireOnce();
      // First paint from whatever is already in the store — a skeleton until
      // load() lands (queueCardState treats an absent slice as loading).
      renderBody();
      // R-031 first-run coachmark on the scope pill. Deferred one frame: the
      // bubble is positioned from the pill's rect, and the pill has no layout
      // until this innerHTML has been through a paint. showCoach() is a no-op
      // on every subsequent run (see the ONCE rule in ui/coachmark.js), so
      // this costs one timer on one boot in a nurse's whole career.
      setTimeout(() => {
        const s = store.get(SLICES.SESSION) || {};
        showCoach({
          id: COACH_SCOPE,
          anchorId: 'today-scope-pill',
          text: COACH_SCOPE_TEXT,
          uid: s.user && s.user.id,
        });
      }, 60);
    },
    load() {
      // Load the staff directory (owner names + colleague list + assign modal)
      // then the queue. loadUsers never throws; a miss just degrades names.
      // Both are RETURNED so Sync (F-NAV-03) can await the real finish line —
      // a busy state that clears while the queue is still in flight is a lie.
      return Promise.all([
        loadUsers().then(() => {
          if (todayMounted()) renderBody();
        }),
        refresh(),
      ]);
    },
  });
}
